<!--
  Copyright (C) 2022 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
-->

<template>
  <!-- Mobile Layout -->
  <AppMobileLayout
    v-if="isMobile"
    :logo="mobileLogo || topbarLogo"
    :logoAlt="mobileLogoAlt || topbarLogoAlt"
    :bottomBarItems="mobileBottomBarItems"
    :profileMenuItems="mobileProfileMenuItems"
    :showLogout="showLogout"
    :logoutLabel="logoutTooltip"
    :loginRouteName="loginRouteName"
    @logout="$emit('logout')"
  >
    <template v-if="$slots['mobile-topbar-start']" #topbar-start>
      <slot name="mobile-topbar-start"></slot>
    </template>
    <template v-if="$slots['mobile-topbar-end']" #topbar-end>
      <slot name="mobile-topbar-end"></slot>
    </template>
    <slot></slot>
    <template v-if="$slots.footer" #footer>
      <slot name="footer"></slot>
    </template>
    <template v-if="$slots['mobile-bottom-bar-extra']" #bottom-bar-extra>
      <slot name="mobile-bottom-bar-extra"></slot>
    </template>
  </AppMobileLayout>

  <!-- Desktop Layout -->
  <div
    v-else
    class="layout-wrapper"
    :class="containerClass"
    @click="onDocumentClick"
  >
    <!-- Sidebar (not shown in horizontal mode on desktop) -->
    <template v-if="showSidebar">
      <!-- Custom sidebar slot for complete override -->
      <slot
        v-if="$slots.sidebar"
        name="sidebar"
        :menuItems="menuItems"
        :isSlimMode="isSlim || isSlimPlus"
        :isCollapsed="layoutState.staticMenuDesktopInactive"
        :onMouseEnter="onSidebarMouseEnter"
        :onMouseLeave="onSidebarMouseLeave"
      ></slot>
      <!-- Default sidebar from lib -->
      <AppSidebar
        v-else
        :menuItems="menuItems"
        :logoFull="sidebarLogoFull"
        :logoIcon="sidebarLogoIcon"
        :logoAlt="sidebarLogoAlt"
        @mouseenter="onSidebarMouseEnter"
        @mouseleave="onSidebarMouseLeave"
      >
        <template #logo="{ isSlimMode }">
          <slot name="sidebar-logo" :isSlimMode="isSlimMode"></slot>
        </template>
        <template #footer>
          <slot name="sidebar-footer"></slot>
        </template>
      </AppSidebar>
    </template>

    <!-- Topbar -->
    <AppTopbar
      :showMenuButton="showMenuButton"
      :showLogo="showTopbarLogo"
      :logo="topbarLogo"
      :logoAlt="topbarLogoAlt"
      :horizontalMenu="isHorizontal && !isSmallScreen"
      :menuItems="menuItems"
      :showSearch="showSearch"
      :searchRoute="searchRoute"
      :searchTooltip="searchTooltip"
      :portalUrl="portalUrl"
      :portalTooltip="portalTooltip"
      :settingsItems="settingsItems"
      :settingsTooltip="settingsTooltip"
      :showLogout="showLogout"
      :logoutTooltip="logoutTooltip"
      :loginRouteName="loginRouteName"
      @toggle-menu="onMenuToggle"
      @logout="$emit('logout')"
    >
      <template #logo>
        <slot name="topbar-logo"></slot>
      </template>
      <template #actions-start>
        <slot name="topbar-actions-start"></slot>
      </template>
      <template #actions-end>
        <slot name="topbar-actions-end"></slot>
      </template>
    </AppTopbar>

    <!-- Main Content -->
    <div class="layout-main">
      <div class="layout-content">
        <slot></slot>
      </div>
      <!-- Footer slot - entirely customizable by consuming app -->
      <slot name="footer"></slot>
    </div>
  </div>
</template>

<script>
import { computed, onMounted, onUnmounted, ref } from "vue";
import { useLayout } from "@/composables/useLayout";
import AppSidebar from "./AppSidebar.vue";
import AppTopbar from "./AppTopbar.vue";
import AppMobileLayout from "./AppMobileLayout.vue";

export default {
  name: "AppLayout",
  components: {
    AppSidebar,
    AppTopbar,
    AppMobileLayout,
  },
  props: {
    // ==========================================
    // Menu Configuration
    // ==========================================
    /**
     * Menu items array for sidebar and horizontal menu
     * Each item: { label, labelKey, icon, to, badge, items }
     */
    menuItems: {
      type: Array,
      default: () => [],
    },

    // ==========================================
    // Sidebar Configuration
    // ==========================================
    /**
     * Full logo for expanded sidebar. Can be overridden with #sidebar-logo slot.
     */
    sidebarLogoFull: {
      type: String,
      default: "",
    },
    /**
     * Icon logo for collapsed sidebar. Can be overridden with #sidebar-logo slot.
     */
    sidebarLogoIcon: {
      type: String,
      default: "",
    },
    /**
     * Sidebar logo alt text
     */
    sidebarLogoAlt: {
      type: String,
      default: "Logo",
    },

    // ==========================================
    // Topbar Configuration
    // ==========================================
    /**
     * Logo for topbar. Can be overridden with #topbar-logo slot.
     */
    topbarLogo: {
      type: String,
      default: "",
    },
    /**
     * Topbar logo alt text
     */
    topbarLogoAlt: {
      type: String,
      default: "Logo",
    },
    /**
     * Show search button in topbar
     */
    showSearch: {
      type: Boolean,
      default: false,
    },
    /**
     * Search route path
     */
    searchRoute: {
      type: String,
      default: "/search",
    },
    /**
     * Search button tooltip
     */
    searchTooltip: {
      type: String,
      default: "Recherche",
    },
    /**
     * Portal URL (external link). Empty to hide.
     */
    portalUrl: {
      type: String,
      default: "",
    },
    /**
     * Portal button tooltip
     */
    portalTooltip: {
      type: String,
      default: "Portail",
    },
    /**
     * Settings menu items
     */
    settingsItems: {
      type: Array,
      default: () => [],
    },
    /**
     * Settings button tooltip
     */
    settingsTooltip: {
      type: String,
      default: "Paramètres",
    },
    /**
     * Show logout button
     */
    showLogout: {
      type: Boolean,
      default: true,
    },
    /**
     * Logout button tooltip
     */
    logoutTooltip: {
      type: String,
      default: "Déconnexion",
    },
    /**
     * Login route name for redirect after logout
     */
    loginRouteName: {
      type: String,
      default: "login",
    },

    // ==========================================
    // Mobile Configuration
    // ==========================================
    /**
     * Logo for mobile topbar
     */
    mobileLogo: {
      type: String,
      default: "",
    },
    /**
     * Mobile logo alt text
     */
    mobileLogoAlt: {
      type: String,
      default: "",
    },
    /**
     * Bottom navigation bar items for mobile
     * Each item: { key, icon, label, labelKey, to, activeMatch }
     */
    mobileBottomBarItems: {
      type: Array,
      default: () => [],
    },
    /**
     * Profile menu items for mobile
     * Each item: { label, labelKey, icon, to, action }
     */
    mobileProfileMenuItems: {
      type: Array,
      default: () => [],
    },
  },
  emits: ["logout"],
  setup() {
    const {
      layoutState,
      containerClass,
      isMobile,
      isSmallScreen,
      isHorizontal,
      isDrawer,
      isSlim,
      isSlimPlus,
      isStatic,
      onMenuToggle,
      hideMobileMenu,
      onDocumentClick,
      onSidebarMouseEnter,
      onSidebarMouseLeave,
      applyLayoutSettings,
    } = useLayout();

    const windowWidth = ref(window.innerWidth);

    const handleResize = () => {
      windowWidth.value = window.innerWidth;
    };

    onMounted(() => {
      applyLayoutSettings();
      window.addEventListener("resize", handleResize);
    });

    onUnmounted(() => {
      window.removeEventListener("resize", handleResize);
    });

    // Show sidebar: always except horizontal mode on desktop
    const showSidebar = computed(() => {
      if (isMobile.value) {
        return true; // Always show sidebar on mobile (will be hidden by CSS until activated)
      }
      if (isSmallScreen.value) {
        return true; // Show sidebar on small screens even in horizontal mode
      }
      return !isHorizontal.value;
    });

    // Show menu button in topbar:
    // - Always on mobile
    // - On desktop small screen (to toggle sidebar)
    // - On desktop static mode
    const showMenuButton = computed(() => {
      if (isMobile.value) {
        return true; // Always show on mobile for sidebar toggle
      }
      if (isSmallScreen.value) {
        return true; // Show on small screen desktop to toggle sidebar
      }
      // On desktop, only show in static mode
      return isStatic.value;
    });

    // Show logo in topbar when sidebar is hidden:
    // - Always on mobile
    // - On small screen desktop when sidebar is hidden
    // - On static mode when sidebar is inactive
    const showTopbarLogo = computed(() => {
      if (isMobile.value) {
        return true; // Always show logo in topbar on mobile
      }
      if (isSmallScreen.value && isStatic.value) {
        return true; // Show logo when sidebar is hidden on small screen
      }
      // On desktop, show logo when static mode has sidebar hidden
      return isStatic.value && layoutState.staticMenuDesktopInactive;
    });

    return {
      layoutState,
      containerClass,
      isMobile,
      isSmallScreen,
      isHorizontal,
      isDrawer,
      isSlim,
      isSlimPlus,
      isStatic,
      showSidebar,
      showMenuButton,
      showTopbarLogo,
      onMenuToggle,
      hideMobileMenu,
      onDocumentClick,
      onSidebarMouseEnter,
      onSidebarMouseLeave,
    };
  },
};
</script>

<style lang="scss">
// Layout Variables
:root {
  --sidebar-width: 18rem;
  --sidebar-width-slim: 4.5rem;
  --sidebar-width-slim-plus: 7rem;
  --topbar-height: 4rem;
}

// Layout Wrapper
.layout-wrapper {
  min-height: 100vh;
}

// Layout Mask (mobile overlay)
.layout-mask {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.4);
  z-index: 998;
  animation: fadeIn 0.2s;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

// Layout Main Content
.layout-main {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  transition: margin-left 0.3s ease;
}

.layout-content {
  flex: 1;
  margin-top: var(--topbar-height);
}

// ==========================================
// STATIC MODE
// ==========================================
.layout-static {
  .layout-sidebar {
    width: var(--sidebar-width);
  }

  .layout-main {
    margin-left: var(--sidebar-width);
  }

  .layout-topbar {
    left: var(--sidebar-width);
    width: calc(100% - var(--sidebar-width));
  }

  // Static Inactive (sidebar completely hidden)
  &.layout-static-inactive {
    .layout-sidebar {
      transform: translateX(-100%);
    }

    .layout-main {
      margin-left: 0;
    }

    .layout-topbar {
      left: 0;
      width: 100%;
    }
  }
}

// ==========================================
// DRAWER MODE
// ==========================================
.layout-drawer {
  .layout-sidebar {
    width: var(--sidebar-width-slim);
    transition:
      width 0.3s ease,
      transform 0.3s ease;

    .menu-label,
    .menu-badge,
    .menu-arrow {
      display: none;
    }

    .menu-subitems {
      display: none;
    }

    .profile-info,
    .profile-arrow,
    .profile-menu {
      display: none;
    }

    .logo-full {
      display: none;
    }

    .logo-icon {
      display: block;
    }

    .sidebar-header {
      padding: 0 0.5rem;
      justify-content: center;
    }

    .menu-link,
    .menu-section-header {
      justify-content: center;
      padding: 0.5rem;
    }

    // Hide toggle button when drawer is collapsed
    .menu-toggle-btn {
      display: none;
    }

    // Make section link fill the header and center icon
    .menu-section-link {
      flex: 1;
      justify-content: center;
      padding: 0.5rem;
    }

    .menu-item,
    .menu-section {
      margin: 0 0.5rem;
    }

    .sidebar-footer {
      display: none;
    }
  }

  .layout-main {
    margin-left: var(--sidebar-width-slim);
  }

  .layout-topbar {
    left: var(--sidebar-width-slim);
    width: calc(100% - var(--sidebar-width-slim));
  }

  // Drawer active (hover) - sidebar expands as overlay
  // Reset all styles to match static mode
  &.layout-drawer-active {
    .layout-sidebar {
      width: var(--sidebar-width);
      z-index: 1000;

      .menu-label,
      .menu-badge,
      .menu-arrow {
        display: block;
      }

      .menu-subitems {
        display: block;
        padding-left: 1rem;
      }

      .profile-info,
      .profile-arrow,
      .profile-menu {
        display: block;
      }

      .logo-full {
        display: block;
      }

      .logo-icon {
        display: none;
      }

      .sidebar-header {
        padding: 0 1rem;
        justify-content: space-between;
      }

      // Reset menu-link to default (like static)
      .menu-link {
        justify-content: flex-start;
        padding: 0.375rem 0.75rem;
      }

      // Reset menu-section-header to default (like static)
      .menu-section-header {
        justify-content: flex-start;
        padding: 0.375rem 0;
      }

      // Show toggle button when drawer is expanded
      .menu-toggle-btn {
        display: flex;
      }

      // Reset section link when drawer is expanded
      .menu-section-link {
        flex: 1;
        justify-content: flex-start;
        padding: 0.375rem 0;
        flex-direction: row;
        gap: 0.75rem;
      }

      .menu-item,
      .menu-section {
        margin: 0 0.5rem;
      }

      // Reset subitems margin
      .menu-subitems .menu-item {
        margin: 0;
      }

      .sidebar-footer {
        display: block;
      }
    }

    // Content doesn't move when drawer is open (overlay)
    .layout-main {
      margin-left: var(--sidebar-width-slim);
    }

    .layout-topbar {
      left: var(--sidebar-width-slim);
      width: calc(100% - var(--sidebar-width-slim));
    }
  }

  // Drawer anchored (pinned) - content shifts
  &.layout-drawer-anchored {
    .layout-sidebar {
      width: var(--sidebar-width);

      .menu-label,
      .menu-badge,
      .menu-arrow {
        display: block;
      }

      .menu-subitems {
        display: block;
        padding-left: 1rem;
      }

      .profile-info,
      .profile-arrow,
      .profile-menu {
        display: block;
      }

      .logo-full {
        display: block;
      }

      .logo-icon {
        display: none;
      }

      .sidebar-header {
        padding: 0 1rem;
        justify-content: space-between;
      }

      // Reset menu-link to default (like static)
      .menu-link {
        justify-content: flex-start;
        padding: 0.375rem 0.75rem;
      }

      // Reset menu-section-header to default (like static)
      .menu-section-header {
        justify-content: flex-start;
        padding: 0.375rem 0;
      }

      // Show toggle button when drawer is anchored
      .menu-toggle-btn {
        display: flex;
      }

      // Reset section link when drawer is anchored
      .menu-section-link {
        flex: 1;
        justify-content: flex-start;
        padding: 0.375rem 0;
        flex-direction: row;
        gap: 0.75rem;
      }

      .menu-item,
      .menu-section {
        margin: 0 0.5rem;
      }

      // Reset subitems margin
      .menu-subitems .menu-item {
        margin: 0;
      }

      .sidebar-footer {
        display: block;
      }
    }

    .layout-main {
      margin-left: var(--sidebar-width);
    }

    .layout-topbar {
      left: var(--sidebar-width);
      width: calc(100% - var(--sidebar-width));
    }
  }
}

// ==========================================
// SLIM MODE
// ==========================================
.layout-slim {
  .layout-sidebar {
    width: var(--sidebar-width-slim);

    .menu-label,
    .menu-badge,
    .menu-arrow {
      display: none;
    }

    .menu-subitems {
      display: none;
    }

    .profile-info,
    .profile-arrow,
    .profile-menu {
      display: none;
    }

    .logo-full {
      display: none;
    }

    .logo-icon {
      display: block;
    }

    .sidebar-header {
      padding: 0 0.5rem;
    }

    .menu-link,
    .menu-section-header {
      justify-content: center;
      padding: 0.5rem;
    }

    // Hide toggle button in slim mode
    .menu-toggle-btn {
      display: none;
    }

    // Make section link fill the header and center icon
    .menu-section-link {
      flex: 1;
      justify-content: center;
      padding: 0.5rem;
    }

    .menu-item,
    .menu-section {
      margin: 0 0.5rem;
    }

    .sidebar-header {
      justify-content: center;
    }

    .sidebar-footer {
      display: none;
    }
  }

  .layout-main {
    margin-left: var(--sidebar-width-slim);
  }

  .layout-topbar {
    left: var(--sidebar-width-slim);
    width: calc(100% - var(--sidebar-width-slim));
  }
}

// ==========================================
// SLIM PLUS MODE
// ==========================================
.layout-slim-plus {
  .layout-sidebar {
    width: var(--sidebar-width-slim-plus);

    .menu-label {
      display: block;
      font-size: 0.9rem;
      text-align: center;
      margin-top: 0.25rem;
      white-space: normal;
      word-wrap: break-word;
      line-height: 1.3;
      color: var(--p-text-muted-color);
    }

    .menu-badge,
    .menu-arrow {
      display: none;
    }

    .menu-subitems {
      display: none;
    }

    .profile-info,
    .profile-arrow,
    .profile-menu {
      display: none;
    }

    .logo-full {
      display: none;
    }

    .logo-icon {
      display: block;
    }

    .sidebar-header {
      padding: 0 0.5rem;
      justify-content: center;
    }

    .menu-link,
    .menu-section-header {
      flex-direction: column;
      justify-content: center;
      padding: 0.375rem 0.5rem;
      gap: 0.25rem;
    }

    // Hide toggle button in slim plus mode
    .menu-toggle-btn {
      display: none;
    }

    // Make section link fill the header and center content vertically
    .menu-section-link {
      flex: 1;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      padding: 0.375rem 0.5rem;
      gap: 0.25rem;
    }

    .menu-icon {
      font-size: 1.5rem;
    }

    .menu-item,
    .menu-section {
      margin: 0 0.5rem;
    }

    .sidebar-footer {
      display: none;
    }
  }

  .layout-main {
    margin-left: var(--sidebar-width-slim-plus);
  }

  .layout-topbar {
    left: var(--sidebar-width-slim-plus);
    width: calc(100% - var(--sidebar-width-slim-plus));
  }
}

// ==========================================
// HORIZONTAL MODE
// ==========================================
.layout-horizontal {
  .layout-sidebar {
    display: none;
  }

  .layout-main {
    margin-left: 0;
  }

  .layout-topbar {
    left: 0;
    width: 100%;
  }
}

// ==========================================
// MOBILE STYLES
// ==========================================
.layout-mobile {
  .layout-sidebar {
    transform: translateX(-100%);
    width: var(--sidebar-width) !important;

    .menu-label,
    .menu-badge {
      display: block !important;
    }

    .profile-info,
    .profile-arrow,
    .profile-menu {
      display: block !important;
    }

    // Hide toggle button and arrow on mobile - menu stays expanded
    .menu-toggle-btn,
    .menu-arrow {
      display: none !important;
    }

    .menu-subitems {
      display: block !important;
    }

    .logo-full {
      display: block !important;
    }

    .logo-icon {
      display: none !important;
    }

    .menu-link,
    .menu-section-header {
      flex-direction: row !important;
      justify-content: flex-start !important;
      padding: 0.375rem 0.75rem !important;
      gap: 0.75rem !important;
    }

    // Section link takes full width on mobile
    .menu-section-link {
      flex: 1 !important;
      padding: 0.375rem 0 !important;
    }

    .menu-icon {
      font-size: 1.25rem !important;
    }
  }

  .layout-main {
    margin-left: 0 !important;
  }

  .layout-topbar {
    left: 0 !important;
    width: 100% !important;
  }

  // Mobile menu active
  &.layout-mobile-active {
    .layout-sidebar {
      transform: translateX(0);
    }
  }
}

// ==========================================
// SMALL SCREEN DESKTOP (sidebar responsive)
// Applied when desktop window is resized to small size
// ==========================================
.layout-small-screen {
  // For static mode on small screen: sidebar hidden by default, can be toggled
  &.layout-static {
    .layout-sidebar {
      transform: translateX(-100%);
      transition: transform 0.3s ease;
      z-index: 1000;
      width: var(--sidebar-width) !important;

      // Ensure full sidebar styles (like mobile)
      .menu-label,
      .menu-badge {
        display: block !important;
      }

      .profile-info,
      .profile-arrow {
        display: block !important;
      }

      .logo-full {
        display: block !important;
      }

      .logo-icon {
        display: none !important;
      }
    }

    .layout-main {
      margin-left: 0;
    }

    .layout-topbar {
      left: 0;
      width: 100%;
    }

    // Show sidebar when toggled active
    &.layout-mobile-active {
      .layout-sidebar {
        transform: translateX(0);
      }
    }
  }

  // For slim/slim-plus modes on small screen: also hide and show as overlay
  &.layout-slim,
  &.layout-slim-plus {
    .layout-sidebar {
      transform: translateX(-100%);
      transition: transform 0.3s ease;
      z-index: 1000;
      width: var(--sidebar-width) !important;

      .menu-label,
      .menu-badge {
        display: block !important;
      }

      .profile-info,
      .profile-arrow {
        display: block !important;
      }

      .logo-full {
        display: block !important;
      }

      .logo-icon {
        display: none !important;
      }

      .menu-link,
      .menu-section-header {
        flex-direction: row !important;
        justify-content: flex-start !important;
        padding: 0.375rem 0.75rem !important;
        gap: 0.75rem !important;
      }
    }

    .layout-main {
      margin-left: 0;
    }

    .layout-topbar {
      left: 0;
      width: 100%;
    }

    &.layout-mobile-active {
      .layout-sidebar {
        transform: translateX(0);
      }
    }
  }

  // For drawer mode: also use overlay style
  &.layout-drawer {
    .layout-sidebar {
      transform: translateX(-100%);
      transition: transform 0.3s ease;
      z-index: 1000;
      width: var(--sidebar-width) !important;

      .menu-label,
      .menu-badge {
        display: block !important;
      }

      .profile-info,
      .profile-arrow {
        display: block !important;
      }

      .logo-full {
        display: block !important;
      }

      .logo-icon {
        display: none !important;
      }
    }

    .layout-main {
      margin-left: 0;
    }

    .layout-topbar {
      left: 0;
      width: 100%;
    }

    &.layout-mobile-active {
      .layout-sidebar {
        transform: translateX(0);
      }
    }
  }

  // For horizontal mode on small screen: hide horizontal menu, show menu button
  &.layout-horizontal {
    .layout-main {
      margin-left: 0;
    }

    .layout-topbar {
      left: 0;
      width: 100%;
    }
  }
}

// ==========================================
// MENU THEMES
// ==========================================
// Auto theme: follows global app theme (uses --p-content-background)
.layout-menu-light {
  // No override needed - inherits from AppSidebar which uses --p-content-background
}

// Contrast theme: opposite of global theme
// In light mode app: shows dark sidebar
// In dark mode app: shows light sidebar
.layout-menu-dark {
  .layout-sidebar {
    --sidebar-bg: #1f2937;
    background-color: var(--sidebar-bg);
    color: #f9fafb;
    border-right-color: #374151;

    .sidebar-header,
    .sidebar-footer,
    .sidebar-profile-section {
      border-color: #374151;
    }

    .profile-name {
      color: #e5e7eb;
    }

    .profile-job {
      color: #9ca3af;
    }

    .profile-header:hover,
    .profile-menu-item:hover {
      background-color: rgba(255, 255, 255, 0.1);
    }

    .profile-menu-item i {
      color: #9ca3af;
    }

    .profile-menu-item span {
      color: #e5e7eb;
    }

    .profile-arrow {
      color: #9ca3af;
    }

    .menu-icon {
      color: #9ca3af;
    }

    .menu-link,
    .menu-section-header {
      color: #e5e7eb;

      &:hover {
        background-color: rgba(255, 255, 255, 0.1);

        .menu-icon {
          color: #f9fafb;
        }
      }
    }

    .menu-item.active .menu-link,
    .menu-section-header.is-active {
      background-color: var(--p-primary-color);
      border-left-color: var(--p-primary-color);

      .menu-icon,
      .menu-label {
        color: var(--p-primary-contrast-color);
      }
    }

    .menu-section-header.has-active-child {
      border-left-color: var(--p-primary-color);
    }
  }

  .layout-topbar {
    background-color: #1f2937;
    border-bottom-color: #374151;

    .topbar-menu-button,
    .topbar-action {
      color: #e5e7eb;

      &:hover {
        background-color: rgba(255, 255, 255, 0.1);
        color: #f9fafb;
      }
    }

    .topbar-menu-item {
      color: #e5e7eb;

      &:hover {
        background-color: rgba(255, 255, 255, 0.1);
        color: #f9fafb;
      }

      &.active {
        color: var(--p-primary-contrast-color);
        background-color: var(--p-primary-color);
      }
    }
  }
}

// When app is dark mode and menu-dark is selected, show light sidebar/topbar instead
html.app-dark .layout-menu-dark {
  .layout-sidebar {
    --sidebar-bg: #ffffff !important;
    background-color: #ffffff !important;
    color: #1f2937 !important;
    border-right-color: #e5e7eb !important;

    .sidebar-header,
    .sidebar-footer,
    .sidebar-profile-section {
      border-color: #e5e7eb !important;
    }

    .profile-name {
      color: #374151 !important;
    }

    .profile-job {
      color: #6b7280 !important;
    }

    .profile-header:hover,
    .profile-menu-item:hover {
      background-color: #f3f4f6 !important;
    }

    .profile-menu-item i {
      color: #6b7280 !important;
    }

    .profile-menu-item span {
      color: #374151 !important;
    }

    .profile-arrow {
      color: #6b7280 !important;
    }

    .menu-icon {
      color: #6b7280 !important;
    }

    .menu-link,
    .menu-section-header {
      color: #374151 !important;

      &:hover {
        background-color: #f3f4f6 !important;

        .menu-icon {
          color: #1f2937 !important;
        }
      }
    }

    .menu-item.active .menu-link,
    .menu-section-header.is-active {
      background-color: var(--p-primary-color) !important;
      border-left-color: var(--p-primary-color) !important;

      .menu-icon,
      .menu-label {
        color: var(--p-primary-contrast-color) !important;
      }
    }

    .menu-section-header.has-active-child {
      border-left-color: var(--p-primary-color) !important;
    }
  }

  .layout-topbar {
    background-color: #ffffff !important;
    border-bottom-color: #e5e7eb !important;

    .topbar-menu-button,
    .topbar-action {
      color: #374151 !important;

      &:hover {
        background-color: #f3f4f6 !important;
        color: #1f2937 !important;
      }
    }

    .topbar-menu-item {
      color: #374151 !important;

      &:hover {
        background-color: #f3f4f6 !important;
        color: #1f2937 !important;
      }

      &.active {
        color: var(--p-primary-contrast-color) !important;
        background-color: var(--p-primary-color) !important;
      }
    }
  }
}

.layout-menu-primary {
  .layout-sidebar {
    --sidebar-bg: var(--p-primary-color);
    background-color: var(--sidebar-bg);
    color: var(--p-primary-contrast-color);
    border-right-color: rgba(255, 255, 255, 0.2);

    .sidebar-header,
    .sidebar-footer,
    .sidebar-profile-section {
      border-color: rgba(255, 255, 255, 0.2);
    }

    .profile-name {
      color: var(--p-primary-contrast-color);
    }

    .profile-job {
      color: rgba(255, 255, 255, 0.7);
    }

    .profile-header:hover,
    .profile-menu-item:hover {
      background-color: rgba(255, 255, 255, 0.15);
    }

    .profile-menu-item i {
      color: rgba(255, 255, 255, 0.7);
    }

    .profile-menu-item span {
      color: var(--p-primary-contrast-color);
    }

    .profile-arrow {
      color: rgba(255, 255, 255, 0.7);
    }

    .menu-icon {
      color: rgba(255, 255, 255, 0.7);
    }

    .menu-link,
    .menu-section-header {
      color: var(--p-primary-contrast-color);

      &:hover {
        background-color: rgba(255, 255, 255, 0.15);

        .menu-icon {
          color: var(--p-primary-contrast-color);
        }
      }
    }

    .menu-item.active .menu-link,
    .menu-section-header.is-active {
      background-color: rgba(255, 255, 255, 0.2);
      border-left-color: var(--p-primary-contrast-color);

      .menu-icon,
      .menu-label {
        color: var(--p-primary-contrast-color);
      }
    }

    .menu-section-header.has-active-child {
      border-left-color: var(--p-primary-contrast-color);
    }
  }

  .layout-topbar {
    background-color: var(--p-primary-color);
    border-bottom-color: rgba(255, 255, 255, 0.2);

    .topbar-menu-button,
    .topbar-action {
      color: rgba(255, 255, 255, 0.7);

      &:hover {
        background-color: rgba(255, 255, 255, 0.15);
        color: var(--p-primary-contrast-color);
      }
    }

    .topbar-menu-item {
      color: var(--p-primary-contrast-color);

      &:hover {
        background-color: rgba(255, 255, 255, 0.15);
      }

      &.active {
        background-color: rgba(255, 255, 255, 0.2);
      }
    }
  }
}
</style>
